/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { isValid } from "date-fns";
import { useBooking } from "./useBooking";
import { useSchedulerParams } from "./useSchedulerParams";
import useWidgetCallbackParams from "./useWidgetCallbackParams";
import usePluginUtils from "./usePluginUtils";
import { useToast } from "../components/ui/Toast";
import { ROUTES, TOAST_ERROR_MESSAGE } from "../constants/constants";
import { areServicesCombinable, isProviderCompatibleWithServices } from "../utils/service";
import { setFlow, FlowType } from "../store/slices/flowSlice";
import { useConfig } from "../lib/react-query/useConfig";
import { getTodayInTimezone, localDate } from "../utils/helpers/date";

const isTodayOrLaterDate = (date, timezone) =>
  /^\d{4}-\d{2}-\d{2}$/.test(date) && isValid(localDate(date)) && date >= getTodayInTimezone(timezone);

const isValidGroupCount = (service, count) =>
  Number.isInteger(count) &&
  count >= (service.min_group_booking || 1) &&
  (!service.limit_booking || count <= service.limit_booking);

export const useSchedulerInitialization = () => {
  const { clearParams, promotionCode, date, count } = useWidgetCallbackParams();
  const {
    schedulerChannel,
    locationData,
    hasLocationParam,
    serviceDataList,
    notFoundServiceIds,
    providerData,
    hasProviderParam,
    isLoading: isDataLoading,
  } = useSchedulerParams();
  const dispatch = useDispatch();
  const { data: config } = useConfig();
  const {
    setSchedulerChannel,
    setSelectedServices,
    setSelectedLocation,
    setSelectedProvider,
    setSelectedDate,
    setPromotionCode,
    setScrollToServiceId,
  } = useBooking();
  const { isPluginEnabled } = usePluginUtils();
  const { toast } = useToast();
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialRoute, setInitialRoute] = useState(null);
  const initializationRef = useRef(false);

  const showErrorToast = useCallback(
    (description) => toast({ title: "Error!", description, variant: "error" }),
    [toast],
  );

  useEffect(() => {
    if (isDataLoading || initializationRef.current) return;

    const flags = {
      isMultiBookingEnabled: isPluginEnabled("multiple_booking"),
      isGroupBookingEnabled: isPluginEnabled("group_booking"),
    };

    let hasError = notFoundServiceIds.length > 0;

    let validServices = serviceDataList;
    if (!areServicesCombinable(validServices, flags)) {
      validServices = [];
      hasError = true;
    }

    const validLocation = locationData?.is_visible !== false ? locationData : null;
    const isLocationMissing = hasLocationParam && isPluginEnabled("location") && !validLocation;
    if (isLocationMissing) hasError = true;

    let validProvider = null;
    if (hasProviderParam) {
      if (providerData && isProviderCompatibleWithServices(providerData, validServices)) {
        validProvider = providerData;
      } else {
        hasError = true;
      }
    }

    if (count > 1) {
      if (flags.isGroupBookingEnabled && validServices.length === 1 && isValidGroupCount(validServices[0], count)) {
        validServices = [{ ...validServices[0], group_booking_count: count }];
      } else {
        hasError = true;
      }
    }

    const hasValidDate = isTodayOrLaterDate(date, config?.timezone);
    if (date && !hasValidDate) hasError = true;
    const canOpenDateTime = hasValidDate && validServices.length > 0 && !!validProvider && !isLocationMissing;

    if (schedulerChannel) setSchedulerChannel(schedulerChannel);
    if (validLocation) setSelectedLocation(validLocation);
    if (validServices.length > 0) {
      setSelectedServices(validServices);
      setScrollToServiceId(validServices[0].id);
    }
    if (validProvider) setSelectedProvider(validProvider);
    if (canOpenDateTime) {
      setSelectedDate(date);
      dispatch(setFlow(FlowType.BOOKING));

      const hasAddonsStep = isPluginEnabled("paid_attributes") && validServices.some((s) => s.has_available_addons);

      setInitialRoute(hasAddonsStep ? ROUTES.ADDONS : ROUTES.DATETIME);
    }
    if (promotionCode) setPromotionCode(promotionCode);
    if (hasError) showErrorToast(TOAST_ERROR_MESSAGE.PRESELECTION_FAILED);

    const hadSchedulerParams =
      !!schedulerChannel ||
      !!locationData ||
      serviceDataList.length > 0 ||
      hasProviderParam ||
      !!promotionCode ||
      !!date ||
      !!count;
    // Skip clearing on payment-callback returns: the status page still needs
    // the hash to read the invoice. Clearing now would drop us on Home.
    const isPaymentCallback = window.location.hash.includes("/ext/invoice-payment/");
    if (hadSchedulerParams && !isPaymentCallback) clearParams();

    initializationRef.current = true;
    setIsInitialized(true);
  }, [
    isDataLoading,
    schedulerChannel,
    locationData,
    hasLocationParam,
    serviceDataList,
    notFoundServiceIds,
    providerData,
    hasProviderParam,
    date,
    count,
    promotionCode,
    config?.timezone,
    isPluginEnabled,
    dispatch,
    setSchedulerChannel,
    setSelectedServices,
    setSelectedLocation,
    setSelectedProvider,
    setSelectedDate,
    setPromotionCode,
    setScrollToServiceId,
    showErrorToast,
    clearParams,
  ]);

  return {
    isLoading: !isInitialized,
    initialRoute,
  };
};
